// src/components/policies/TermlyEmbed.js
import React, { useEffect, useRef, useState } from 'react';

const TERM_SCRIPT_ID  = 'termly-jssdk';
const TERM_SCRIPT_SRC = 'https://app.termly.io/embed-policy.min.js';

const reloadTermlyScript = () =>
  new Promise((resolve, reject) => {
    const stale = document.getElementById(TERM_SCRIPT_ID);
    if (stale) stale.remove();
    try { delete window.Termly; } catch (_) { window.Termly = undefined; }

    const script  = document.createElement('script');
    script.id     = TERM_SCRIPT_ID;
    script.type   = 'text/javascript';
    script.src    = `${TERM_SCRIPT_SRC}?v=${Date.now()}`;
    script.async  = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Termly script failed to load.'));
    document.body.appendChild(script);
  });

/**
 * Returns true when the Termly iframe inside `el` has rendered visible content.
 * The iFrameResizer injects wrapper elements even on a 410/empty response, so we
 * can't rely on childElementCount alone — we need the iframe to have height > 0.
 */
const hasVisibleContent = (el) => {
  if (!el) return false;
  const iframe = el.querySelector('iframe');
  if (!iframe) return false;
  // iFrameResizer sets an explicit height style once content renders
  const h = parseInt(iframe.style.height || '0', 10);
  return h > 50;
};

/* ─── component ─────────────────────────────────────────────────────────────── */
const TermlyEmbed = ({ dataId }) => {
  const [loading,  setLoading]  = useState(true);
  const [hasError, setHasError] = useState(false);
  const embedRef               = useRef(null);
  const mountedRef             = useRef(false);

  const run = () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setHasError(false);

    let observer   = null;
    let heightPoll = null;

    const confirm = () => {
      // Called after iFrameResizer has had time to set a height.
      // If the iframe has real height → show content; otherwise → show error.
      if (!mountedRef.current) return;
      if (hasVisibleContent(embedRef.current)) {
        setLoading(false);
      } else {
        setLoading(false);
        setHasError(true);
      }
    };

    if (embedRef.current) {
      observer = new MutationObserver(() => {
        // Termly / iFrameResizer injected something — wait a moment for the
        // iframe to receive its height from the resizer, then verify.
        if (embedRef.current?.querySelector('iframe')) {
          observer.disconnect();
          // Poll briefly for iFrameResizer to set height (up to ~3 s)
          let ticks = 0;
          heightPoll = setInterval(() => {
            ticks++;
            if (!mountedRef.current) { clearInterval(heightPoll); return; }
            if (hasVisibleContent(embedRef.current) || ticks >= 15) {
              clearInterval(heightPoll);
              confirm();
            }
          }, 200);
        }
      });
      observer.observe(embedRef.current, { childList: true, subtree: true });
    }

    // Absolute hard timeout — give up after 12 s no matter what
    const timeout = setTimeout(() => {
      if (!mountedRef.current) return;
      observer?.disconnect();
      clearInterval(heightPoll);
      confirm();
    }, 12000);

    reloadTermlyScript()
      .then(() => {
        if (!mountedRef.current) return;
        setTimeout(() => {
          if (typeof window.Termly?.initialize === 'function') {
            window.Termly.initialize();
          }
        }, 100);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        clearTimeout(timeout);
        observer?.disconnect();
        clearInterval(heightPoll);
        setLoading(false);
        setHasError(true);
      });

    return () => {
      clearTimeout(timeout);
      observer?.disconnect();
      clearInterval(heightPoll);
    };
  };

  useEffect(() => {
    mountedRef.current = true;
    const cleanup = run();
    return () => {
      mountedRef.current = false;
      cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataId]);

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-12">
      <div className="relative min-h-[320px]">

        {/* Spinner overlay — floats above the embed div so the iframe always
            has full-size room to render, even while loading */}
        {loading && !hasError && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-white">
            <svg
              className="animate-spin w-8 h-8 text-brand-red"
              xmlns="http://www.w3.org/2000/svg"
              fill="none" viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10"
                stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <p className="text-sm text-gray-400">Loading policy…</p>
          </div>
        )}

        {/* Termly target — never hidden or collapsed */}
        <div ref={embedRef} name="termly-embed" data-id={dataId} />

        {/* Error state */}
        {hasError && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-6 py-10 text-center">
            <p className="text-sm font-semibold text-gray-800 mb-2">
              This policy couldn&rsquo;t be loaded right now.
            </p>
            <p className="text-xs text-gray-500 leading-relaxed max-w-sm mx-auto">
              The policy document may be unavailable or the embed link may need
              to be refreshed. If you manage this site, please check that the
              policy is published in your Termly account.
            </p>
            <button
              onClick={() => run()}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-red text-white text-xs font-semibold hover:bg-red-700 transition-colors"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TermlyEmbed;
